// TODO: clean up https://gapi.storyblok.com/v1/api.
import { getGlobalConfigElement } from '../../utils/get-global-config-element';
import { retry } from '../../utils/retry'; // Assuming this is a utility function for retrying async operations
import { LanguageService } from '../language';
// import { StoryblokService } from '../storyblok';
// Ensure these types are defined in your types file
import { PageItem, PageItems, Query, TeaserItems, TeaserQueryResult } from './types';

export class TeaserService {
  static nonDefaultThemeSubcategories = ['events', 'life-talks'];

  static async getTeaser(teaserId: number): Promise<PageItem> {
    const fetchTeaser: () => Promise<PageItem> = async () => {
      const response = await fetch(
        'https://gapi.storyblok.com/v1/api',
        this.createRequest(this.createTeaserByIdQuery(teaserId)),
      );
      const result = await response.json();
      return this.parseQueryResult(result);
    };

    return retry(fetchTeaser);
  }

  static async getTeasers(
    tags: string[],
    groupTags: string[] = [],
    sortBy: string,
    page: number,
    perPage: number,
  ): Promise<PageItems> {
    return tags.length > 0 && groupTags.length > 0
      ? this.fetchByAnd(
          tags,
          groupTags,
          sortBy,
          page,
          perPage,
          this.createTeaserByTypeQuery.bind(this),
          this.parseQueryResults.bind(this),
        )
      : this.fetchByOr(
          tags,
          groupTags,
          sortBy,
          page,
          perPage,
          this.createTeaserByTypeQuery.bind(this),
          this.parseQueryResults.bind(this),
        );
  }

  static async fetchByOr<Content, QueryResult>(
    tags: string[],
    groupTags: string[],
    sortBy: string,
    page: number,
    perPage: number,
    createQuery: (tags: string[], groupTags: string[], sortBy: string, page: number, perPage: number) => Query,
    parseResult: (queryResult: QueryResult) => TeaserItems<Content>,
  ): Promise<TeaserItems<Content>> {
    const fetchTeasers = async () => {
      const response = await fetch(
        'https://gapi.storyblok.com/v1/api',
        this.createRequest(createQuery(tags, groupTags, sortBy, page, perPage)),
      );
      const result = await response.json();
      return parseResult(result);
    };

    return retry(fetchTeasers);
  }

  static async fetchByAnd<Content, QueryResult>(
    tags: string[],
    groupTags: string[],
    sortBy: string,
    page: number,
    perPage: number,
    createQuery: (tags: string[], groupTags: string[], sortBy: string, page: number, perPage: number) => Query,
    parseResult: (queryResult: QueryResult) => TeaserItems<Content>,
  ): Promise<TeaserItems<Content>> {
    const allQueries = groupTags.flatMap((groupTag) =>
      tags.map((tag) => async (): Promise<TeaserItems<Content>> => {
        const fetchTeasers = async () => {
          const response = await fetch(
            'https://gapi.storyblok.com/v1/api',
            this.createRequest(createQuery([tag], [groupTag], sortBy, page, perPage)),
          );
          const result = await response.json();
          return parseResult(result);
        };

        return retry(fetchTeasers);
      }),
    );

    const responses = await Promise.all(allQueries.map((query) => query()));
    const mergedResponse = responses.reduce(
      (accumulator: TeaserItems<Content> & { idList: number[] }, response: TeaserItems<Content>) => {
        const { items } = response;
        const uniqueItems = items.filter((item) => !accumulator.idList.includes(item?.id));
        const duplicatedItems = items.filter((item) => accumulator.idList.includes(item?.id));
        const mergedTotal = accumulator.total + response.total - duplicatedItems.length;

        return {
          idList: [...accumulator.idList, ...uniqueItems.map((item) => item.id)],
          items: [...accumulator.items, ...uniqueItems],
          total: mergedTotal,
        };
      },
      { idList: [], items: [], total: 0 } as TeaserItems<Content> & {
        idList: number[];
      },
    );

    // TODO: Remove the idList before returning the merged response
    // eslint-disable-next-line
    const { idList, ...result } = mergedResponse;
    return result;
  }

  static parseQueryResult(queryResult: TeaserQueryResult): PageItem {
    const { PageItem } = queryResult.data;
    if (!PageItem) {
      throw new Error('PageItem not found in the query result');
    }
    return PageItem;
  }

  static parseQueryResults(queryResult: TeaserQueryResult): PageItems {
    const { PageItems } = queryResult.data;

    if (!PageItems) {
      throw new Error('PageItems not found in the query result');
    }
    return {
      items: [...PageItems.items],
      total: PageItems.total,
    };
  }

  static createRequestHeaders() {
    return {
      token: process.env.GATSBY_STORYBLOK_SPACE_API_KEY as string,
      version: 'published',
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
  }

  static createRequest(query: { query: string }) {
    return {
      method: 'POST',
      mode: 'cors',
      headers: this.createRequestHeaders(),
      body: JSON.stringify(query),
    };
  }

  static createLanguageQueryParameter(): string {
    const globalConfigurationElement = getGlobalConfigElement();
    const allSpaceLocales = globalConfigurationElement?.dataset.allSpaceLocales || '[]';
    const locale = globalConfigurationElement.dataset.locale;

    if (!LanguageService.isPageDefaultLanguage(locale)) {
      return `starts_with: "${LanguageService.getActiveLanguage()}/"`;
    }

    const excludeList = JSON.parse(allSpaceLocales)
      .filter((locale: string) => locale !== LanguageService.getDefaultLocale())
      .map((locale: string) => `${locale}/*`);

    return `excluding_slugs: "${excludeList.join(',')}"`;
  }

  static createTagQueryParameter(tags: string[], groupTags: string[]): string {
    const tagQuery = tags.length > 0 ? `tag: {in_array: "${tags.join(',')}"},` : '';
    const groupTagQuery =
      tags.length > 0
        ? `tag_group: {all_in_array: "${groupTags.join(',')}"}`
        : `tag_group: {in_array: "${groupTags.join(',')}"}`;

    return `filter_query_v2: {${tagQuery} ${groupTagQuery} },`;
  }

  static createTeaserByTypeQuery(
    tags: string[],
    groupTags: string[],
    sortBy: string,
    page: number,
    perPage: number,
  ): Query {
    const storyblokStoryId = getGlobalConfigElement()?.dataset.storyblokStoryId || '';
    const parameters = `
      ${this.createTagQueryParameter(tags, groupTags)}
      sort_by: "${sortBy}",
      page: ${page},
      per_page: ${perPage},
      excluding_ids: "${storyblokStoryId}",
      ${this.createLanguageQueryParameter()}
    `;

    return {
      query: `{
        PageItems(${parameters}) {
          items {
            content {
              tag
              tag_group
              teaser_title
              teaser_overline
              teaser_description
              teaser_image
            }
            published_at
            default_full_slug
            id
            first_published_at
          }
          total
        }
      }`,
    };
  }

  static createTeaserByIdQuery(teaserId: number): Query {
    const parameters = `id: ${teaserId}`;
    return {
      query: `{
        PageItem(${parameters}) {
          full_slug
          content {
            tag
            tag_group
            teaser_title
            teaser_overline
            teaser_description
            teaser_image
          }
        }
      }`,
    };
  }
}
